import siteService from "./siteService";
import { useViewerStore } from "../store/store";
import { DesignData } from "../@types/types";
import { pickCustomerSiteRestrictions } from "../helpers/customerSiteRestrictions";

/**
 * API often omits or lags `Customer_Site_Restrictions` on GET; keep in-store / cached envelope
 * in sync so finalize + design-data refetch do not wipe notes the user still has locally.
 */
function mergeCustomerSiteRestrictionsFromRetrieve(
  response: any[],
  pickedFromApi: string,
): { next: string; siteDesignDetails: any[] } {
  const prev = (useViewerStore.getState().customerSiteRestrictions ?? "").trim();
  const next = pickedFromApi.trim() !== "" ? pickedFromApi : prev;

  if (!Array.isArray(response) || response.length === 0) {
    return { next, siteDesignDetails: response };
  }

  if (next.trim() === "") {
    return { next, siteDesignDetails: response };
  }

  const siteRow = response[0];
  return {
    next,
    siteDesignDetails: [
      {
        ...siteRow,
        Customer_Site_Restrictions: next,
        customer_Site_Restrictions: next,
      },
      ...response.slice(1),
    ],
  };
}

export async function getDesignVersionList(salesforceId: string): Promise<DesignData[] | null> {
  const response = await siteService.retrieveSiteDesignDetails(salesforceId);
  if (response?.Message === "No records found") {
    useViewerStore.setState({
      siteDesignDetails: response,
      rsaNotesFromDesign: "",
      rsaStatusFromDesign: undefined,
      customerSiteRestrictions: ""
    });
    return null;
  }

  if(response[0].NonFinalizedDesign.length > 0){
    useViewerStore.setState({
      isNonFinalizedSavedDesign: true,
      nonFinalizedDesignData: response[0].NonFinalizedDesign[0].Design[0],
      rsaNotesFromDesign: response[0].RSA_Notes?.slice(0, 255) ?? "",
      rsaStatusFromDesign: response[0].RSA_Status,
    });
    const nfDesign = response[0].NonFinalizedDesign[0].Design[0];
    const picked = pickCustomerSiteRestrictions(nfDesign, response[0]);
    const { next, siteDesignDetails } = mergeCustomerSiteRestrictionsFromRetrieve(response, picked);
    useViewerStore.setState({
      isNonFinalizedSavedDesign: true,
      nonFinalizedDesignData: nfDesign,
      siteDesignDetails,
      customerSiteRestrictions: next,
    });
    return null;
  }

  const picked = pickCustomerSiteRestrictions(response[0].Design[0], response[0]);
  const { next, siteDesignDetails } = mergeCustomerSiteRestrictionsFromRetrieve(response, picked);

  useViewerStore.setState({
    selectedLossesVendor: response[0].Design[0].MultipliersVendor,
    efficiencyOverride: response[0].Design[0].TsrfEfficiencyOverride,
    SFannualUsage: response[0].Design[0].Buildings[0].Max_System_Size_Electric_Load,
    siteBuildingData: response[0].Design[0].Buildings,
    siteDesignDetails,
    rsaNotesFromDesign: response[0].RSA_Notes?.slice(0, 255) ?? "",
    customerSiteRestrictions: next,
    rsaStatusFromDesign: response[0].RSA_Status,
  });

  const activeDesign = response[0].Design;
  const inactiveDesign = response[0].InactiveDesign;

  inactiveDesign.sort((a: any, b: any) => {
    if (a.Design_No > b.Design_No) {
      return -1;
    } else if (a.Design_No < b.Design_No) {
      return 1;
    } else {
      return b.Version_No - a.Version_No;
    }
  });

  return [...activeDesign, ...inactiveDesign];
}
